'use client';
// import axios from 'axios';
import { Menu, X } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ContactButton } from '@/components/ui/ContactButton';
import { GetStartedButton } from '@/components/ui/GetStartedButton';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { getOptimalImageUrl } from '@/lib/utils';
import { components } from '@/types/strapi';
import { DesktopNavigation } from '../mega-menu/desktop-navigation';
import { MobileNavigation } from '../mega-menu/mobile-navigation';

type GlobalNavbarComponent = components['schemas']['GlobalNavbarComponent'];
interface HeaderProps extends GlobalNavbarComponent {
	logo: components['schemas']['Logo'];
}

export default function Header({
	logo,
	right_navbar_items,
	megamenu,
}: {
	logo: HeaderProps['logo'];
	right_navbar_items: components['schemas']['SharedButtonComponent'][];
	megamenu: components['schemas']['GlobalMegamenuComponent'][];
}) {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const optimalLogoImg = getOptimalImageUrl(logo.image);

	return (
		<header
			className={`sticky top-0 z-50 w-full bg-white/90 shadow-sm backdrop-blur-md transition-all duration-300 supports-[backdrop-filter]:bg-white/50`}>
			<nav
				className="container mx-auto px-4 sm:px-6 lg:px-8"
				aria-label="Main navigation">
				<div className="flex h-16 items-center justify-between">
					{logo && optimalLogoImg && optimalLogoImg.url && (
						<div className="-ml-2 -mt-2 flex shrink-0 items-center lg:-ml-4">
							<Link
								href={logo.url || ''}
								className="flex-shrink-0">
								<Image
									src={optimalLogoImg.url}
									alt={logo.image.name || 'Logo'}
									width={120}
									height={30}
									className={'h-8 w-auto'}
									priority
								/>
							</Link>
						</div>
					)}
					<div className="hidden lg:flex lg:flex-1 lg:justify-center">
						<DesktopNavigation
							megamenu={
								(megamenu as components['schemas']['GlobalMegamenuComponent'][]) ||
								[]
							}
						/>
					</div>

					{/* Action buttons and mobile menu */}
					<div className="flex shrink-0 items-center gap-2 md:gap-4">
						{right_navbar_items &&
							right_navbar_items.length > 0 && (
								<GetStartedButton
									URL={right_navbar_items[0].URL}
									text={right_navbar_items[0].text}
								/>
							)}
						{right_navbar_items &&
							right_navbar_items.length > 1 &&
							right_navbar_items[1] && (
								<ContactButton
									URL={
										right_navbar_items[1].URL ||
										'tel:+91 9876543210'
									}
									text={
										right_navbar_items[1].text ||
										'Contact Us'
									}
								/>
							)}
						{/* Contact button - responsive styling handled in component
						{/* <ContactButton /> */}

						{/* Mobile menu button - only visible on mobile */}
						<Sheet
							open={mobileMenuOpen}
							onOpenChange={setMobileMenuOpen}>
							<SheetTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-10 w-10 lg:hidden"
									aria-label="Open menu">
									<Menu className="h-10 w-10" />
								</Button>
							</SheetTrigger>
							<SheetTitle></SheetTitle>
							<SheetContent
								side="right"
								className="w-[85vw] max-w-sm border-l p-0 shadow-xl"
								onPointerDownOutside={() =>
									setMobileMenuOpen(false)
								}>
								<SheetHeader className="sticky top-0 z-10 border-b bg-white/95 p-4 backdrop-blur-sm">
									<div className="flex items-center justify-between">
										{logo &&
											logo.image &&
											logo.image.url && (
												<div className="-ml-2 -mt-2 flex shrink-0 items-center lg:-ml-4">
													<Link
														href={logo.url || '/'}
														className="flex-shrink-0">
														<Image
															src={logo.image.url}
															alt={
																logo.image
																	.name ||
																'Logo'
															}
															width={120}
															height={30}
															className={
																'h-8 w-auto'
															}
															priority
														/>
													</Link>
												</div>
											)}
										<SheetClose asChild>
											<Button
												variant="ghost"
												size="icon"
												className="rounded-full hover:bg-gray-100">
												<X className="h-5 w-5" />
												<span className="sr-only">
													Close menu
												</span>
											</Button>
										</SheetClose>
									</div>
								</SheetHeader>
								<div className="max-h-[calc(100vh-5rem)] overflow-y-auto px-4 py-6">
									<MobileNavigation
										megamenu={
											(megamenu as components['schemas']['GlobalMegamenuComponent'][]) ||
											[]
										}
									/>
									<div className="mt-8 space-y-4">
										{right_navbar_items &&
											right_navbar_items.length > 0 && (
												<GetStartedButton
													variant="mobile"
													URL={
														right_navbar_items[0]
															.URL
													}
													text={
														right_navbar_items[0]
															.text
													}
												/>
											)}
										{right_navbar_items &&
											right_navbar_items.length > 1 &&
											right_navbar_items[1] && (
												<ContactButton
													variant="mobile"
													URL={
														right_navbar_items[1]
															.URL ||
														'+91 9876543210'
													}
													text={
														right_navbar_items[1]
															.text ||
														'Contact Us'
													}
												/>
											)}
									</div>
								</div>
							</SheetContent>
						</Sheet>
					</div>
				</div>
			</nav>
		</header>
	);
}
